import {
  MessageSquare, Zap, SmilePlus, Globe, History as HistoryIcon,
  ListChecks, Code as CodeIcon, ScanFace, ClipboardList, HeartPulse,
} from "lucide-react";

// One entry per live-session type — the single source of truth SessionRoom
// reads to configure the shared JitsiSession wrapper and the shared
// SessionDock, so each type keeps its own previously-working Jitsi
// behavior (see JitsiSession.jsx's docstring) and its own contextual tool
// icons (never all types' tools at once — that was explicitly ruled out).
export const SESSION_TYPES = {
  meeting: {
    label: "Meeting",
    collapsedIcon: MessageSquare,
    jitsi: {
      scriptSrc: "https://meet.okotech.ai/external_api.js",
      configOverwrite: {
        prejoinPageEnabled: true,
        defaultRemoteDisplayName: "Participant",
        defaultLocalDisplayName: "You",
      },
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS: ["microphone", "camera", "desktop", "chat", "raisehand", "participants-pane", "tileview", "recording", "hangup"],
      },
    },
    tools: [
      { key: "transcript", icon: MessageSquare, label: "Transcript & Notes" },
      { key: "catchup",    icon: Zap,           label: "Catch Me Up" },
      { key: "emotions",   icon: SmilePlus,     label: "Live Emotions" },
      { key: "captions",   icon: Globe,         label: "Live Captions" },
      { key: "history",    icon: HistoryIcon,   label: "Previous Sessions", recurringOnly: true },
    ],
  },

  interview: {
    label: "Interview",
    collapsedIcon: MessageSquare,
    jitsi: {
      scriptSrc: "https://meet.okotech.ai/external_api.js",
      configOverwrite: {
        prejoinPageEnabled: true,
        disableFilmstrip: true,
        defaultRemoteDisplayName: "",
        defaultLocalDisplayName: "",
        chatAlignment: "right",
      },
      interfaceConfigOverwrite: {
        FILM_STRIP_ENABLED: false,
        CHAT_ALIGNMENT: "right",
        VIDEO_LAYOUT_FIT: "both",
        CHAT_HEIGHT: "50%",
        SIDETOOLBAR_HEIGHT: "50vh",
        TOOLBAR_BUTTONS: ["microphone", "camera", "hangup", "participants-pane", "desktop", "recording"],
      },
      autoOpenChat: true,
      autoOpenParticipants: true,
    },
    tools: [
      { key: "transcript", icon: MessageSquare, label: "Transcript" },
      { key: "questions",  icon: ListChecks,     label: "Questions & Scoring", interviewerOnly: true },
      { key: "code",       icon: CodeIcon,       label: "Code Editor", standalone: true },
    ],
  },

  ekyc: {
    label: "eKYC Verification",
    collapsedIcon: ListChecks,
    jitsi: {
      scriptSrc: "https://meet.jit.si/external_api.js", // pre-existing — left as-is, out of scope to change
      configOverwrite: { prejoinPageEnabled: false },
      interfaceConfigOverwrite: { SHOW_JITSI_WATERMARK: false, SHOW_BRAND_WATERMARK: false },
      needsRef: true,
    },
    tools: [
      { key: "progress", icon: ListChecks, label: "Verification Progress" },
      { key: "results",  icon: ScanFace,   label: "Verification Results" },
    ],
  },

  healthcare: {
    label: "Healthcare Session",
    collapsedIcon: HeartPulse,
    jitsi: {
      scriptSrc: "https://meet.jit.si/external_api.js", // pre-existing — left as-is, out of scope to change
      configOverwrite: {
        prejoinPageEnabled: true,
        defaultRemoteDisplayName: "",
        defaultLocalDisplayName: "",
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        enableClosePage: false,
      },
      interfaceConfigOverwrite: {
        FILM_STRIP_ENABLED: false,
        CHAT_ALIGNMENT: "right",
        VIDEO_LAYOUT_FIT: "both",
        CHAT_HEIGHT: "50%",
        SIDETOOLBAR_HEIGHT: "50vh",
        TOOLBAR_BUTTONS: ["microphone", "camera", "hangup", "participants-pane", "desktop", "recording", "chat"],
      },
    },
    tools: [
      { key: "background", icon: ClipboardList, label: "Patient Background" },
      { key: "vitals",     icon: HeartPulse,     label: "Live AI Vitals" },
    ],
  },
};

export const isValidSessionType = (type) => Object.prototype.hasOwnProperty.call(SESSION_TYPES, type);
